import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MaterialWithStock } from '@components/organisms/MaterialCatalogTable';
import { MaterialCategory } from '@models/warehouse.model';
import { generateMaterialCatalogPdfFilename } from './pdfFilename';

export interface MaterialCatalogPdfOptions {
  materials: MaterialWithStock[];
  categories: MaterialCategory[];
  includeCostAndValue?: boolean;
}

// Color constants (matching Tailwind theme)
const PRIMARY: [number, number, number] = [79, 70, 229]; // indigo-600
const HEADER_BG: [number, number, number] = [249, 250, 251]; // gray-50
const LOW_STOCK_BG: [number, number, number] = [255, 251, 235]; // amber-50
const OUT_OF_STOCK_BG: [number, number, number] = [254, 242, 242]; // red-50
const TEXT_PRIMARY: [number, number, number] = [17, 24, 39]; // gray-900
const TEXT_SECONDARY: [number, number, number] = [107, 114, 128]; // gray-500
const GREEN: [number, number, number] = [22, 163, 74]; // green-600
const AMBER: [number, number, number] = [217, 119, 6]; // amber-600
const RED: [number, number, number] = [220, 38, 38]; // red-600

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

export function generateMaterialCatalogPdf({
  materials,
  categories,
  includeCostAndValue = true,
}: MaterialCatalogPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return '-';
    return categories.find((c) => c.categoryId === categoryId)?.name || '-';
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'in_stock': return 'In Stock';
      case 'low_stock': return 'Low Stock';
      case 'out_of_stock': return 'Out of Stock';
      default: return '-';
    }
  };

  // Summary stats
  const totalItems = materials.length;
  const totalValue = materials.reduce((sum, m) => sum + m.inventoryValue, 0);
  const lowStockCount = materials.filter((m) => m.stockStatus === 'low_stock').length;
  const outOfStockCount = materials.filter((m) => m.stockStatus === 'out_of_stock').length;

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Material Catalog Report', margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // SUMMARY STATS (4 cards)
  // ========================
  const statsY = 28;
  const cardGap = 4;
  const statsCount = includeCostAndValue ? 4 : 3;
  const cardWidth = (pageWidth - margin * 2 - cardGap * (statsCount - 1)) / statsCount;
  const cardHeight = 18;

  const stats = [
    { label: 'Total Items', value: formatNumber(totalItems), color: PRIMARY },
    ...(includeCostAndValue
      ? [{ label: 'Inventory Value', value: formatCurrency(totalValue), color: GREEN }]
      : []),
    { label: 'Low Stock', value: formatNumber(lowStockCount), color: AMBER },
    { label: 'Out of Stock', value: formatNumber(outOfStockCount), color: RED },
  ];

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);

    // Card bg
    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    // Label
    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    // Value
    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE
  // ========================
  const tableColumns = [
    { header: 'Name', dataKey: 'name' },
    { header: 'SKU', dataKey: 'sku' },
    { header: 'Category', dataKey: 'category' },
    { header: 'On Hand', dataKey: 'onHand' },
    { header: 'Available', dataKey: 'available' },
    ...(includeCostAndValue
      ? [
          { header: 'Avg Cost', dataKey: 'avgCost' },
          { header: 'Value', dataKey: 'value' },
        ]
      : []),
    { header: 'Status', dataKey: 'status' },
  ];

  const tableRows = materials.map((m) => ({
    name: m.name,
    sku: m.sku || '-',
    category: getCategoryName(m.categoryId),
    onHand: formatNumber(m.totalOnHand),
    available: formatNumber(m.totalAvailable),
    avgCost: formatCurrency(m.averageCost || 0),
    value: formatCurrency(m.inventoryValue),
    status: getStatusLabel(m.stockStatus),
    _stockStatus: m.stockStatus,
  }));

  autoTable(doc, {
    startY: statsY + cardHeight + 8,
    columns: tableColumns,
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      onHand: { halign: 'right' },
      available: { halign: 'right' },
      avgCost: { halign: 'right' },
      value: { halign: 'right' },
      status: { halign: 'center' },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = tableRows[data.row.index];
        if (!row) return;

        // Row background for low/out of stock
        if (row._stockStatus === 'low_stock') {
          data.cell.styles.fillColor = LOW_STOCK_BG;
        } else if (row._stockStatus === 'out_of_stock') {
          data.cell.styles.fillColor = OUT_OF_STOCK_BG;
        }

        // Status column color coding
        if (data.column.dataKey === 'status') {
          if (row._stockStatus === 'in_stock') {
            data.cell.styles.textColor = GREEN;
          } else if (row._stockStatus === 'low_stock') {
            data.cell.styles.textColor = AMBER;
          } else if (row._stockStatus === 'out_of_stock') {
            data.cell.styles.textColor = RED;
          }
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    didDrawPage: () => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;

      // Footer line
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      // Footer text
      doc.setFontSize(7);
      doc.setTextColor(...TEXT_SECONDARY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${today}`, margin, pageHeight - 7);
      doc.text(
        `Page ${pageNumber} of {total_pages}`,
        pageWidth - margin,
        pageHeight - 7,
        { align: 'right' },
      );
    },
  });

  // Replace total pages placeholder
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  // Save
  const filename = generateMaterialCatalogPdfFilename();
  doc.save(filename);
}
